/**
 * SFrame220 – Distribute Assets: hub dashboard + phase shortcuts (Stream J).
 */

import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { PhaseOverview, type PhaseStepState } from '../settlement';
import { useSettlementSession } from '../../hooks/useSettlementSession';
import { useSettlement } from '../../contexts/SettlementContext';
import { SETTLEMENT_PHASE_CONFIGS } from '../../utils/settlementFlowConfig';
import { SETTLEMENT_PLANNER_LAYOUT } from '../settlement/settlementTheme';
import {
  extractBusinessFrom160,
  extractFinancialAccountsFromSettlementData,
  extractPersonalPropertyFrom160,
  extractRealEstate155,
  extractVehicles155,
  plannerFinancialFallbackRows,
  type StreamJUnifiedStatus,
} from './distribution/streamJSources';
import { RowDocumentPanel } from './distribution/streamJRowDocs';

const config = SETTLEMENT_PHASE_CONFIGS['distribute'];

const stepRoutes: Record<string, string> = {
  'dist-1': '/sframe-221', // Manage Assets During Settlement
  'dist-2': '/sframe-222', // Create Distribution Plan
  'dist-3': '/sframe-230', // Distribute Personal Property
  'dist-4': '/sframe-235', // Distribute Financial Accounts
  'dist-5': '/sframe-240', // Transfer or Sell Property, Vehicles & Businesses
  'dist-6': '/sframe-245', // Special Distributions & Interim Payments
};

const DASHSTAT: StreamJUnifiedStatus[] = ['not_started', 'in_progress', 'completed', 'blocked'];
const DLAB: Record<StreamJUnifiedStatus, string> = {
  not_started: 'Not Started',
  in_progress: 'In Progress',
  completed: 'Completed',
  blocked: 'Blocked',
};

type FT = 'all' | StreamJUnifiedStatus;
interface RowUx {
  status: StreamJUnifiedStatus;
  notes: string;
  blockerReason: string;
}
interface S220Persist {
  version: 1;
  rows: Record<string, RowUx>;
}
function emp(): S220Persist {
  return { version: 1, rows: {} };
}
function load220(raw: unknown): S220Persist {
  const b = emp();
  if (!raw || typeof raw !== 'object') return b;
  const rows = (raw as { rows?: unknown }).rows;
  if (!rows || typeof rows !== 'object') return b;
  for (const [k, val] of Object.entries(rows)) {
    if (!val || typeof val !== 'object') continue;
    const o = val as Record<string, unknown>;
    const st = String(o.status) as StreamJUnifiedStatus;
    b.rows[k] = {
      status: DASHSTAT.indexOf(st) >= 0 ? st : 'not_started',
      notes: String(o.notes ?? ''),
      blockerReason: String(o.blockerReason ?? ''),
    };
  }
  return b;
}

type Built = {
  key: string;
  section: 'Personal property' | 'Financial accounts' | 'Real estate & vehicles';
  name: string;
  type: string;
  ben: string;
  method: string;
  initialStatus?: StreamJUnifiedStatus;
};

export default function SFrame220() {
  const { estateId } = useParams<{ estateId: string }>();
  const navigate = useNavigate();
  const { isArchived } = useSettlement();
  const { loadPhaseProgress, loadFrameData, saveFrameData, loadPlannerData, error } = useSettlementSession(
    estateId ?? null,
    { readOnly: isArchived }
  );

  const [steps, setSteps] = useState<PhaseStepState[]>(() =>
    config.steps.map((s) => ({
      step: s,
      status: 'not-started',
      subTaskProgress: { completed: 0, total: s.subTasks?.reduce((acc, st) => acc + (st.checklistItems?.length || 0), 0) || 0 },
    }))
  );
  const [persist, setPersist] = useState<S220Persist>(emp());
  const [loaded220, setLoaded220] = useState(false);
  const [rows, setRows] = useState<Built[]>([]);
  const [filter, setFilter] = useState<FT>('all');
  const [open, setOpen] = useState<string | null>(null);
  const [plannerFin, setPlannerFin] = useState(false);

  useEffect(() => {
    if (!estateId) return;
    (async () => {
      const data = await loadPhaseProgress('distribute');
      setSteps(
        config.steps.map((s) => ({
          step: s,
          status: (data.find((d) => d.stepId === s.id)?.status as PhaseStepState['status']) ?? 'not-started',
          subTaskProgress: {
            completed: data.find((d) => d.stepId === s.id)?.completedChecklistItems?.length ?? 0,
            total: s.subTasks?.reduce((acc, st) => acc + (st.checklistItems?.length || 0), 0) || 0,
          },
        }))
      );
    })();
  }, [estateId, loadPhaseProgress]);

  useEffect(() => {
    setLoaded220(false);
    if (!estateId) {
      setPersist(emp());
      setLoaded220(true);
      return;
    }
    let cancelled = false;
    (async () => {
      const fd = await loadFrameData('sframe-220');
      if (!cancelled) {
        setPersist(load220(fd?.data));
        setLoaded220(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [estateId, loadFrameData]);

  useEffect(() => {
    if (!estateId) return;
    let cancelled = false;
    (async () => {
      const [r160, r155, r146, r144] = await Promise.all([
        loadFrameData('sframe-160'),
        loadFrameData('sframe-155'),
        loadFrameData('sframe-146'),
        loadFrameData('sframe-144'),
      ]);
      const d160 = (r160?.data as Record<string, unknown>) ?? null;
      const d155 = (r155?.data as Record<string, unknown>) ?? null;
      const d146 = (r146?.data as Record<string, unknown>) ?? null;
      const d144 = (r144?.data as Record<string, unknown>) ?? null;
      const pp = extractPersonalPropertyFrom160(d160);
      let fin = extractFinancialAccountsFromSettlementData(d146);
      if (!fin.length) fin = extractFinancialAccountsFromSettlementData(d144);
      fin = fin.map((a) => ({ ...a }));
      let plan = false;
      if (!fin.length) {
        fin = await plannerFinancialFallbackRows((id) => loadPlannerData(id));
        plan = fin.length > 0;
      }
      const re = extractRealEstate155(d155);
      const ve = extractVehicles155(d155);
      const biz = extractBusinessFrom160(d160);
      const built: Built[] = [];
      pp.forEach((p) =>
        built.push({
          key: `personal:${p.id}`,
          section: 'Personal property',
          name: p.item,
          type: p.category,
          ben: p.beneficiaryDisplay,
          method: p.distributionMethod,
        })
      );
      fin.forEach((f) =>
        built.push({
          key: `financial:${f.id}`,
          section: 'Financial accounts',
          name: f.label,
          type: f.accountType,
          ben: f.beneficiaryDisplay,
          method: f.distributionMethod,
        })
      );
      re.forEach((r) =>
        built.push({
          key: `property:re:${r.id}`,
          section: 'Real estate & vehicles',
          name: r.address,
          type: 'Real estate',
          ben: r.beneficiaryDisplay,
          method: r.distributionMethod,
          initialStatus: r.dashboardStatus,
        })
      );
      ve.forEach((v) =>
        built.push({
          key: `property:veh:${v.id}`,
          section: 'Real estate & vehicles',
          name: v.description,
          type: 'Vehicle',
          ben: v.beneficiaryDisplay,
          method: v.distributionMethod,
          initialStatus: v.dashboardStatus,
        })
      );
      biz.forEach((b) =>
        built.push({
          key: `property:bz:${b.id}`,
          section: 'Real estate & vehicles',
          name: b.name,
          type: 'Business interest',
          ben: b.beneficiaryDisplay,
          method: b.distributionMethod,
        })
      );
      if (!cancelled) {
        setPlannerFin(plan);
        setRows(built);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [estateId, loadFrameData, loadPlannerData]);

  const initialStatusByKey = useMemo(() => {
    const map = new Map<string, StreamJUnifiedStatus>();

    for (const row of rows) {
      if (row.initialStatus) {
        map.set(row.key, row.initialStatus);
      }
    }

    return map;
  }, [rows]);

  const rowState = useCallback(
    (k: string): RowUx =>
      persist.rows[k] ?? {
        status: initialStatusByKey.get(k) ?? 'not_started',
        notes: '',
        blockerReason: '',
      },
    [persist.rows, initialStatusByKey]
  );

  const patchRow = useCallback((k: string, p: Partial<RowUx>) => {
    setPersist((prev) => {
      const base = prev.rows[k] ?? {
        status: initialStatusByKey.get(k) ?? 'not_started',
        notes: '',
        blockerReason: '',
      };
      return {
        ...prev,
        rows: {
          ...prev.rows,
          [k]: { ...base, ...p },
        },
      };
    });
  }, [initialStatusByKey]);

  useEffect(() => {
    if (!estateId || isArchived || !loaded220) return;
    const t = window.setTimeout(() => void saveFrameData('sframe-220', persist as unknown as Record<string, unknown>), 460);
    return () => window.clearTimeout(t);
  }, [persist, estateId, isArchived, loaded220, saveFrameData]);

  const progress = useMemo(() => {
    const n = rows.length;
    if (!n) return { done: 0, pct: 0 };
    const done = rows.reduce((acc, r) => acc + (rowState(r.key).status === 'completed' ? 1 : 0), 0);
    return { done, pct: Math.round((done / n) * 100) };
  }, [rows, rowState]);

  const filt = useMemo(() => {
    if (filter === 'all') return rows;
    return rows.filter((r) => rowState(r.key).status === filter);
  }, [rows, filter, rowState]);

  const handleNav = (id: string) => {
    const path = stepRoutes[id];
    if (path) navigate(estateId ? `${path}/${estateId}` : path);
  };

  const ei = estateId ?? '';

  function renderBand(title: Built['section'], hint: string) {
    const list = filt.filter((r) => r.section === title);
    if (!list.length) return null;
    return (
      <section className="mb-10">
        <div className="mb-3">
          <h2 className="text-lg font-semibold text-blue-900">{title}</h2>
          <p className="text-xs text-stone-600">{hint}</p>
        </div>
        <div className="overflow-x-auto rounded-xl border border-stone-200 bg-white">
          <table className="min-w-full text-sm">
            <thead className="bg-stone-100 text-[11px] uppercase tracking-wide">
              <tr>
                <th className="text-left px-4 py-2">Asset / label</th>
                <th className="text-left px-4 py-2 hidden sm:table-cell">Type</th>
                <th className="text-left px-4 py-2 hidden md:table-cell">Beneficiary</th>
                <th className="text-left px-4 py-2 hidden xl:table-cell">Method</th>
                <th className="text-left px-4 py-2">Status</th>
                <th className="w-14" aria-hidden />
              </tr>
            </thead>
            <tbody>
              {list.map((item) => {
                const ux = rowState(item.key);
                const exp = open === item.key;
                return (
                  <Fragment key={item.key}>
                    <tr className="border-b border-stone-100 hover:bg-stone-50">
                      <td className="py-3 px-4">{item.name}</td>
                      <td className="py-3 px-4 hidden sm:table-cell">{item.type}</td>
                      <td className="py-3 px-4 hidden md:table-cell">{item.ben}</td>
                      <td className="py-3 px-4 hidden xl:table-cell">{item.method}</td>
                      <td className="py-3 px-4">
                        <select
                          className="text-sm border border-stone-200 rounded-md px-2 py-1 bg-white max-w-[10rem]"
                          disabled={!estateId || isArchived}
                          value={ux.status}
                          onChange={(e) => patchRow(item.key, { status: e.target.value as StreamJUnifiedStatus })}
                        >
                          {DASHSTAT.map((v) => (
                            <option key={v} value={v}>{DLAB[v]}</option>
                          ))}
                        </select>
                      </td>
                      <td className="py-3 px-2 text-right">
                        <button type="button" aria-label="toggle" className="p-2 text-stone-400 hover:text-stone-600" onClick={() => setOpen((c) => (c === item.key ? null : item.key))}>
                          {exp ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </button>
                      </td>
                    </tr>
                    {exp && (
                      <tr className="bg-stone-50/80 border-b border-stone-100">
                        <td colSpan={6} className="px-4 py-4">
                          <div className="grid gap-4 lg:grid-cols-2">
                            <textarea
                              className="rounded-lg border border-stone-200 px-3 py-2 text-sm min-h-[72px]"
                              placeholder="Operational notes…"
                              disabled={!estateId || isArchived}
                              value={ux.notes}
                              onChange={(e) => patchRow(item.key, { notes: e.target.value })}
                            />
                            {ux.status === 'blocked' ? (
                              <textarea
                                className="rounded-lg border border-red-100 px-3 py-2 text-sm min-h-[72px]"
                                placeholder="Blocker rationale…"
                                disabled={!estateId || isArchived}
                                value={ux.blockerReason}
                                onChange={(e) => patchRow(item.key, { blockerReason: e.target.value })}
                              />
                            ) : null}
                          </div>
                          <RowDocumentPanel estateId={estateId ?? null} phase="distribute" stepId={`dash:${item.key}`} readOnly={isArchived} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>
    );
  }

  const emptyDash = loaded220 && rows.length === 0;

  return (
    <div className="space-y-8">
      {error && (
        <div className={`${SETTLEMENT_PLANNER_LAYOUT.container} mb-4`}>
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">{error}</div>
        </div>
      )}
      <div className="rounded-2xl border border-stone-200 bg-white p-5 md:p-6 shadow-sm">
        <div className="mb-8 flex flex-col gap-6 lg:flex-row lg:justify-between lg:items-end">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-stone-600">Execution hub</p>
            <h1 className="text-2xl font-semibold text-blue-900 mt-2">Central distribution cockpit</h1>
            <p className="text-sm text-stone-600 mt-2 max-w-3xl">
              Three synchronized inventories feed read-only allocations while fiduciary teams push live execution states into{' '}
              <code className="text-xs">sframe-220</code>.
            </p>
          </div>
          <div className="text-right">
            <p className="text-xs uppercase text-stone-500">Completed rows</p>
            <p className="text-4xl font-semibold text-blue-900">
              {progress.done}/{rows.length}{' '}
              <span className="text-base font-normal text-stone-600">({progress.pct}%)</span>
            </p>
          </div>
        </div>
        <div className="mb-10 flex flex-wrap gap-2">
          {(['all'] as FT[]).concat(DASHSTAT).map((tab) => (
            <button
              key={String(tab)}
              type="button"
              onClick={() => setFilter(tab)}
              className={`rounded-full border px-4 py-1.5 text-xs font-semibold ${filter === tab ? 'border-blue-950 bg-blue-900 text-white' : 'border-stone-300 bg-white'}`}
            >
              {tab === 'all' ? 'All' : DLAB[tab]}
            </button>
          ))}
        </div>
        {emptyDash ? (
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-5 py-6 text-sm text-amber-950">
            <p className="font-semibold mb-2">Upstream inventories incomplete.</p>
            {ei ? (
              <ul className="list-disc pl-6 space-y-1">
                <li>
                  <Link className="text-blue-900 font-semibold" to={`/sframe-160/${ei}`}>Personal property • business (160)</Link>
                </li>
                <li>
                  <Link className="text-blue-900 font-semibold" to={`/sframe-146/${ei}`}>Financial accounts (route 146)</Link>
                </li>
                <li>
                  <Link className="text-blue-900 font-semibold" to={`/sframe-155/${ei}`}>Real estate &amp; autos (155)</Link>
                </li>
              </ul>
            ) : (
              <p>Select an estate workspace to enable deep-links.</p>
            )}
          </div>
        ) : (
          <>
            {plannerFin && ei ? (
              <p className="text-xs text-stone-500 mb-6">
                No tabular payloads found in settlement frame <code className="text-[11px]">sframe-146</code>; provisional planner-derived financial rows hydrate this panel until executor entries exist.
              </p>
            ) : null}
            {renderBand('Personal property', 'Personal items from executor frame sframe-160.')}
            {renderBand('Financial accounts', 'Executor frame sframe-146 supplemented by planners when sparse.')}
            {renderBand('Real estate & vehicles', 'Structures + vehicles captured on step 155; business disclosures from step 160.')}
          </>
        )}
      </div>
      <PhaseOverview
        title="Distribute Assets"
        subtitle="Execute the distribution of assets based on the will, beneficiary designations, and estate readiness."
        layoutVariant="planner"
        sectionOrder="next-first"
        steps={steps}
        estimatedMinutes={steps.filter((s) => s.status !== 'complete').reduce((sum, x) => sum + (x.step.estimatedMinutes || 0), 0)}
        onStepClick={handleNav}
      />
    </div>
  );
}